import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";
import { APP_LANG_STORAGE_KEY, resolveAppLang } from "@/lib/detect-lang";
import type { Lang } from "@/lib/level-utils";

interface LanguageState {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (es: string, en: string) => string;
}

const LanguageContext = createContext<LanguageState>({
  lang: "es",
  setLang: () => {},
  t: (es) => es,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { member } = useAuth();
  const [lang, setLangState] = useState<Lang>(() => {
    if (member?.language_pref === "es" || member?.language_pref === "en") {
      return member.language_pref;
    }
    return resolveAppLang();
  });

  useEffect(() => {
    if (member?.language_pref === "es" || member?.language_pref === "en") {
      setLangState(member.language_pref);
    }
  }, [member?.language_pref]);

  useEffect(() => {
    document.documentElement.lang = lang;
    window.localStorage.setItem(APP_LANG_STORAGE_KEY, lang);
  }, [lang]);

  const setLang = useCallback(async (newLang: Lang) => {
    setLangState(newLang);
    if (member?.id) {
      await supabase.rpc("update_member_language", { new_lang: newLang });
    }
  }, [member?.id]);

  const t = useCallback((es: string, en: string) => (lang === "es" ? es : en), [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
